import { parseTimeToHMS, parseBooleanCell } from "./fmxFieldTypes";
import { ASSET_CONDITION_MAP, ASSET_CONDITION_UNSUPPORTED, ASSET_CATEGORY_MAP } from "./fmxFieldMetadata";
import { detectHeaderRow, uniqueHeaders } from "./sheetParse";

export function parseCSV(raw) {
  const text = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  const rows = []; let i = 0;
  const parseRow = () => {
    const cells = [];
    while (i < text.length && text[i] !== "\n") {
      if (text[i] === '"') {
        i++; let val = "";
        while (i < text.length) {
          if (text[i] === '"' && text[i+1] === '"') { val += '"'; i += 2; }
          else if (text[i] === '"') { i++; break; }
          else val += text[i++];
        }
        cells.push(val);
        if (text[i] === ",") i++;
      } else {
        let val = "";
        while (i < text.length && text[i] !== "," && text[i] !== "\n") val += text[i++];
        cells.push(val.trim());
        if (text[i] === ",") i++;
      }
    }
    if (text[i] === "\n") i++;
    return cells;
  };
  while (i < text.length) rows.push(parseRow());
  if (rows.length < 2) return { headers: [], rows: [] };
  // Headers aren't always on the first line — exports often carry title or
  // banner rows above the real header row. Blank/duplicate header names get
  // unique placeholders so columns don't collapse into one object key.
  const trimmed = rows.map(r => r.map(c => c.trim()));
  const headerRow = detectHeaderRow(trimmed);
  const headers = uniqueHeaders(trimmed[headerRow]);
  const dataRows = rows.slice(headerRow + 1).filter(r => r.some(c => c.trim() !== "")).map(cells =>
    Object.fromEntries(headers.map((h, j) => [h, cells[j] ?? ""]))
  );
  return { headers, rows: dataRows, headerRow };
}

export function buildMappedRows(rows, mapping, transformRules, allFields) {
  return rows.map(row => {
    const out = {};
    allFields.forEach(f => {
      if (transformRules[f.name]) return;
      const col = mapping[f.name];
      out[f.name] = col ? (row[col] ?? "") : "";
    });
    Object.entries(transformRules).forEach(([fName, rule]) => {
      if (rule.type === "formula" && rule.code) {
        try { const fn = new Function("row", "mapping", `"use strict"; ${rule.code}`); out[fName] = fn(row, mapping) ?? ""; }
        catch { out[fName] = ""; }
      }
    });
    return out;
  });
}

// depCacheMap: { [crossSheetType]: string[] } — live FMX names keyed by crossSheet label.
// schemaType: the current import type (e.g. "Building", "Equipment") — its own type is excluded
//             from dependency validation so a Building import doesn't flag Building fields.
export function computeCellErrors(rows, allFields, schemaType, depCacheMap) {
  // Strip module qualifier: "Work Request:maintenance" → "Work Request" + "maintenance"
  const sepIdx = schemaType ? schemaType.indexOf(':') : -1;
  const skipCrossSheet = schemaType ? (sepIdx === -1 ? schemaType : schemaType.slice(0, sepIdx)) : null;
  const moduleSlug = sepIdx === -1 ? null : schemaType.slice(sepIdx + 1);

  const cellMap = {};
  allFields.forEach(f => {
    // Dependency names normalized once per field, the same way push-time
    // matchDepLookup resolves them (lowercase + trimmed), so Validate never
    // flags a value that the push would resolve fine. Prefer the module-scoped
    // bucket (e.g. `Request Type:maintenance`) over the aggregate.
    let depNameSet = null;
    if (f.crossSheet && f.crossSheet !== skipCrossSheet) {
      const names = (moduleSlug && depCacheMap?.[`${f.crossSheet}:${moduleSlug}`]) || depCacheMap?.[f.crossSheet];
      if (names && names.length > 0) {
        depNameSet = new Set(names.map(n => String(n).toLowerCase().trim()));
      }
    }

    rows.forEach((row, ri) => {
      const val = row[f.name] ?? "";
      const key = `${ri}-${f.name}`;
      if (f.isCustomField) return;
      if (f.required && !val) { cellMap[key] = "error"; return; }
      if (!val) return;
      if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { cellMap[key] = "error"; return; }
      if (f.type === "date" && isNaN(Date.parse(val))) { cellMap[key] = "error"; return; }
      if (f.type === "number" && isNaN(Number(val))) { cellMap[key] = "error"; return; }
      if (f.type === "time" && parseTimeToHMS(val) === null) { cellMap[key] = "error"; return; }
      if (f.type === "boolean" && parseBooleanCell(val) === undefined) { cellMap[key] = "error"; return; }
      // Asset condition: FMX has no "retired" condition (warn — the field is
      // omitted at push); anything not in the enum map or 1-6 is an error.
      if (f.special === "assetConditionEnum") {
        const normalized = String(val).toLowerCase().trim();
        if (ASSET_CONDITION_UNSUPPORTED.includes(normalized)) { cellMap[key] = "warning"; return; }
        const n = parseInt(val, 10);
        if (ASSET_CONDITION_MAP[normalized] === undefined && !(n >= 1 && n <= 6)) { cellMap[key] = "error"; return; }
      }
      // Asset category: must be a known capital-planning category label or the
      // bare enum index 0-11; anything else is an error.
      if (f.special === "assetCategoryEnum") {
        const normalized = String(val).toLowerCase().trim();
        const n = parseInt(val, 10);
        if (ASSET_CATEGORY_MAP[normalized] === undefined && !(n >= 0 && n <= 11)) { cellMap[key] = "error"; return; }
      }
      if (f.maximumLength && String(val).length > f.maximumLength) { cellMap[key] = "warning"; return; }
      // Dependency error: crossSheet field whose value doesn't appear in FMX live cache.
      // Array lookups split on the same delimiters the push transform uses.
      if (depNameSet) {
        const vals = (f.lookupConfig?.isArray || f.type === 'stringArray')
          ? String(val).split(/[;,]/).map(s => s.trim()).filter(Boolean)
          : [String(val).trim()];
        if (vals.some(v => !depNameSet.has(v.toLowerCase()))) {
          cellMap[key] = "dep_error";
        }
      }
    });
  });
  return cellMap;
}

// ---- import-record summarizers (shared by MigrationTab / App / ProjectScreen) ----

// Latest import record per schema_type by completed_at.
export function latestImportPerSchema(imports) {
  const latest = new Map();
  for (const rec of imports || []) {
    const prev = latest.get(rec.schema_type);
    if (!prev || new Date(rec.completed_at || 0) > new Date(prev.completed_at || 0)) {
      latest.set(rec.schema_type, rec);
    }
  }
  return latest;
}

// Rows an import represents: actual pushed count when a push ran, else the
// stored/mapped count (download-only or pre-push_status records).
export function importRowsCount(rec) {
  if (!rec) return 0;
  return (rec.push_status != null ? rec.pushed_count : null) ?? rec.row_count_stored ?? rec.row_count ?? 0;
}

// Lifecycle of an import record: 'pushed' (all rows landed), 'partial' (some
// or all rows failed), 'mapped' (saved/downloaded but never pushed).
export function importPushState(rec) {
  if (!rec) return null;
  if (rec.push_status === 'pushed') return 'pushed';
  if (rec.push_status === 'partial' || rec.push_status === 'failed') return 'partial';
  return 'mapped';
}

export function toCSV(headers, rows) {
  const esc = v => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s; };
  return [headers.map(esc).join(","), ...rows.map(r => headers.map(h => esc(r[h] ?? "")).join(","))].join("\n");
}

export function downloadCSV(filename, headers, rows) {
  const blob = new Blob([toCSV(headers, rows)], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export function suggestMapping(headers, fields) {
  const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const map = {};
  fields.forEach(f => {
    const m = headers.find(h => norm(h) === norm(f.name) || norm(h).includes(norm(f.name)) || norm(f.name).includes(norm(h)));
    if (m) map[f.name] = m;
  });
  return map;
}
